import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import PlanTier, { PlanTierProps, PlanType } from "./PlanTier";
import {
  calculateImages,
  CREDITS_FIRST_ANIMATION,
  getOneOffTierConfig,
  getSubscriptionTierConfig,
} from "@repo/api/utils/billing";
import { SubscriptionTierType } from "@repo/api/billing";
import { TeamBillingInfoDto } from "@repo/api/team";
import { CouponDto } from "@repo/api/coupon";
import CouponCodeInput from "./CouponCodeInput";
import { useState, useEffect } from "react";
import { getCouponBenefitText } from "@repo/api/utils/coupon";

type Props = {
  type: PlanType;
  missingCredits?: number;
  activeSubscription: TeamBillingInfoDto["activeSubscription"] | null;
  onCreateCheckoutSuccess?: () => void;
  teamExternalId: string;
  redirectVideoExternalId?: string;
  autoApplyCoupon: CouponDto | null;
  currentCoupon: CouponDto | null;
  setCurrentCoupon: (coupon: CouponDto | null) => void;
  currentCredits: number;
};

const SUBSCRIPTION_TIERS: SubscriptionTierType[] = [
  "MONTHLY_29",
  "MONTHLY_49",
  "MONTHLY_99",
  "MONTHLY_149",
  "MONTHLY_249",
  "MONTHLY_499",
  "MONTHLY_799",
  "MONTHLY_1199",
];
const ONE_OFF_CREDITS = [2900, 4900, 9900, 14900, 24900, 49900, 79900, 119900];

export default function PlanTiersTab({
  type,
  missingCredits,
  activeSubscription,
  onCreateCheckoutSuccess,
  teamExternalId,
  redirectVideoExternalId,
  autoApplyCoupon,
  currentCoupon,
  setCurrentCoupon,
  currentCredits,
}: Props) {
  const tiers = getTierProps(
    type,
    activeSubscription,
    teamExternalId,
    currentCoupon,
    redirectVideoExternalId,
    onCreateCheckoutSuccess,
  );

  const showForThisVideoTier =
    type === "ONE_OFF" && missingCredits && missingCredits > 0;

  const currentImages = calculateImages(currentCredits);

  return (
    <div className="flex w-full flex-col items-center overflow-x-hidden pt-10 pb-12">
      <DialogTitle className="text-center text-2xl font-bold">
        Select a credit package
      </DialogTitle>
      <DialogDescription className="mt-1 text-center text-base text-text-secondary">
        1 image ={" "}
        <span className="font-semibold">{CREDITS_FIRST_ANIMATION} credits</span>
        {currentImages > 0 && (
          <>
            . You currently have credits for{" "}
            <span className="font-semibold">
              {currentImages} {currentImages === 1 ? "image" : "images"}.
            </span>
          </>
        )}
      </DialogDescription>
      <div className="mt-8 w-full px-8">
        {currentCoupon && (
          <CouponDeal planType={type} currentCoupon={currentCoupon} />
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {showForThisVideoTier ? (
            <PlanTier
              type="ONE_OFF"
              price={getOneOffTierConfig(missingCredits).price}
              credits={missingCredits}
              currency={getOneOffTierConfig(missingCredits).currency}
              badge="FOR_THIS_VIDEO"
              onCreateCheckoutSuccess={onCreateCheckoutSuccess}
              teamExternalId={teamExternalId}
              redirectVideoExternalId={redirectVideoExternalId}
              currentCoupon={currentCoupon}
            />
          ) : null}
          {tiers.map((tier) => (
            <PlanTier key={tier.type + tier.credits} {...tier} />
          ))}
        </div>
      </div>
      <div className="mt-6 self-start px-8">
        <CouponCodeInput
          autoApplyCoupon={autoApplyCoupon}
          currentCoupon={currentCoupon}
          teamExternalId={teamExternalId}
          setCurrentCoupon={setCurrentCoupon}
        />
      </div>
    </div>
  );
}

function getTierProps(
  type: Props["type"],
  activeSubscription: TeamBillingInfoDto["activeSubscription"] | null,
  teamExternalId: string,
  currentCoupon: CouponDto | null,
  redirectVideoExternalId?: string,
  onCreateCheckoutSuccess?: () => void,
): PlanTierProps[] {
  if (type === "SUBSCRIPTION") {
    return SUBSCRIPTION_TIERS.map((tier) => {
      const tierConfig = getSubscriptionTierConfig(tier);
      return {
        type: "SUBSCRIPTION",
        price: tierConfig.price,
        credits: tierConfig.credits,
        currency: tierConfig.currency,
        subscriptionTier: tier,
        activeSubscriptionTier: activeSubscription?.tier,
        badge: getSubscriptionBadge(tier, activeSubscription),
        onCreateCheckoutSuccess,
        teamExternalId,
        redirectVideoExternalId,
        currentCoupon,
      };
    });
  }

  return ONE_OFF_CREDITS.map((credits) => {
    const tierConfig = getOneOffTierConfig(credits);
    return {
      type: "ONE_OFF",
      price: tierConfig.price,
      credits: tierConfig.credits,
      currency: tierConfig.currency,
      badge: getOneOffBadge(credits),
      onCreateCheckoutSuccess,
      teamExternalId,
      redirectVideoExternalId,
      currentCoupon,
    };
  });
}

function getSubscriptionBadge(
  tier: SubscriptionTierType,
  activeSubscription: TeamBillingInfoDto["activeSubscription"] | null,
) {
  if (activeSubscription) {
    return activeSubscription.tier === tier
      ? "CURRENT_SUBSCRIPTION"
      : undefined;
  }

  if (tier === "MONTHLY_49") {
    return "MOST_POPULAR";
  }

  return undefined;
}

function getOneOffBadge(credits: number) {
  if (credits === 4900) {
    return "MOST_POPULAR";
  }

  return undefined;
}

function CouponDeal({
  planType,
  currentCoupon,
}: {
  planType: PlanType;
  currentCoupon: CouponDto;
}) {
  const timeLeft = useDealCountdown(currentCoupon.expiresAt);

  const isForThisPlan = currentCoupon.config.planTypes.includes(planType);

  if (!isForThisPlan) {
    return null;
  }

  return (
    <div className="mb-5 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-tertiary-150 px-6 py-3 sm:flex-row sm:items-center">
      <div className="flex flex-1 items-center gap-4">
        <img src="/package-deal.svg" alt="Deal" className="h-9 w-9" />
        <div className="mt-0.5 flex-1">
          <p className="text-sm leading-none font-semibold text-tertiary-400">
            {currentCoupon.config.campaignTitle ?? "Coupon applied!"}
          </p>
          <p className="mt-1 text-sm leading-none text-tertiary-400">
            {getCouponBenefitText(planType, currentCoupon)}
          </p>
        </div>
      </div>

      {timeLeft && (
        <div className="flex items-center justify-end gap-2.5">
          <DealTimeLabel value={timeLeft.days} label="Days" />
          <DealTimeLabel value={timeLeft.hours} label="Hours" />
          <DealTimeLabel value={timeLeft.minutes} label="Mins" />
          <DealTimeLabel value={timeLeft.seconds} label="Secs" />
        </div>
      )}
    </div>
  );
}

function DealTimeLabel({ value, label }: { value: number; label: string }) {
  const paddedValue = value.toString().padStart(2, "0");

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-sm bg-tertiary-400">
        <div className="absolute top-1/2 right-0 bottom-0 left-0 bg-black/10" />
        <p className="relative text-base font-semibold text-white">
          {paddedValue}
        </p>
      </div>
      <p className="text-xs leading-none font-medium text-tertiary-400">
        {label}
      </p>
    </div>
  );
}

function useDealCountdown(expiresAt: string | null) {
  const [timeLeft, setTimeLeft] = useState(calculateDealTimeLeft(expiresAt));

  useEffect(() => {
    const interval = setInterval(() => {
      const timeLeft = calculateDealTimeLeft(expiresAt);
      setTimeLeft(timeLeft);
    }, 250);

    return () => clearInterval(interval);
  }, [expiresAt]);

  return timeLeft;
}

function calculateDealTimeLeft(expiresAt: string | null) {
  if (!expiresAt) {
    return null;
  }

  const dealEnd = new Date(expiresAt);
  const diffInMs = dealEnd.getTime() - Date.now();

  if (diffInMs <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  const totalSeconds = Math.floor(diffInMs / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
  };
}
